import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import type { AssociationType } from '../../core/SchemaClient';
import type { PropertySchemaMap } from '../../hooks/usePropertySchema';
import type {
  DetailField,
  EntityTypeConfig,
} from '../../record/registry/entityTypeRegistry';
import { HIDDEN_ASSOCIATION_TARGETS } from '../../record/registry/fieldConstants';
import { isAssociationVisibleOnOverview } from '../../utils/associationMatching';
import {
  dropsEmptyProperty,
  isVisiblePropertyKey,
} from './buildDetailFieldList';

type ReadOptions = {
  mode: 'read';
  config: EntityTypeConfig;
  schema: PropertySchemaMap;
  object: CrmObject;
  assocAccessMap: Map<string, string>;
  /** See buildDetailFieldList's ReadOptions. */
  keepEmptyPropertyKeys?: ReadonlySet<string>;
  /** See buildDetailFieldList's ReadOptions. */
  keepEmptyAssociationKeys?: ReadonlySet<string>;
  /** Map from association_type_name → target_entity_type_name for filtering hidden targets */
  associationTargetMap?: Map<string, string>;
  /** entity_type_name -> entity_kind[] (from buildEntityKindMap). */
  entityKindMap: ReadonlyMap<string, readonly string[]>;
  /** association_type_name -> full AssociationType, for kind resolution. */
  assocTypeByName: Map<string, AssociationType>;
};

type EditOptions = {
  mode: 'edit';
  config: EntityTypeConfig;
  schema: PropertySchemaMap;
  editableAssocTypeNames: Set<string>;
  editableAssociationTypes: Array<{ association_type_name: string }>;
};

export type BuildFallbackFieldListOptions = ReadOptions | EditOptions;

/**
 * Check whether an association type name points to a hidden target.
 * Uses the target map when available; otherwise falls back to checking
 * if the association type name does NOT end with a primary entity type
 * suffix (defensive heuristic for when target map is not provided).
 *
 * Exported (along with `passesReadKindFilter` and
 * `appendVisibleAssociations` below) because the ordered-field path in
 * `buildDetailFieldList.ts` applies the same carve-outs to
 * `detailFieldOrder`-enumerated associations — one shared implementation
 * for both the ordered and fallback read paths.
 */
export function isHiddenAssociation(
  assocTypeName: string,
  targetMap?: Map<string, string>
): boolean {
  if (!targetMap) {
    return false;
  }
  const target = targetMap.get(assocTypeName);
  // WORKAROUND until #12411: see fieldConstants.ts HIDDEN_ASSOCIATION_TARGETS
  return !!target && HIDDEN_ASSOCIATION_TARGETS.has(target);
}

/**
 * Kind-based allowlist gate, taking only the two maps it actually needs.
 *
 * Split out of `passesReadKindFilter` so callers that don't have (and can't
 * cheaply build) a full `ReadOptions` can apply the identical rule — notably
 * `overview-section`, which pre-filters `object.associations` BEFORE handing
 * them to `useAssociations`, so it never hydrates targets the row filter is
 * about to drop. One implementation, so the fetch-side and render-side gates
 * cannot drift.
 *
 * Fails closed: unknown association type (not yet loaded) is hidden rather
 * than leaked — overview-section gates rows until assocTypeByName is ready.
 */
export function passesOverviewKindFilter(
  assocTypeName: string,
  assocTypeByName: Map<string, AssociationType>,
  entityKindMap: ReadonlyMap<string, readonly string[]>
): boolean {
  const at = assocTypeByName.get(assocTypeName);
  if (!at) {
    return false;
  }
  return isAssociationVisibleOnOverview(at, entityKindMap);
}

/**
 * Kind-based allowlist gate for read-mode association rows.
 * Fails closed: unknown association type (not yet loaded) is hidden rather
 * than leaked — overview-section gates rows until assocTypeByName is ready.
 */
export function passesReadKindFilter(
  assocTypeName: string,
  opts: ReadOptions
): boolean {
  return passesOverviewKindFilter(
    assocTypeName,
    opts.assocTypeByName,
    opts.entityKindMap
  );
}

/**
 * Iterate object.associations and return the visible association DetailFields,
 * applying the four read-mode carve-outs and de-duplicating against
 * `alreadyEmitted`. `alreadyEmitted` is mutated: every association_type_name
 * encountered is added (before carve-outs) so repeated types and already-
 * enumerated types are never emitted twice. Shared by the ordered and fallback
 * read paths so their association rules can never drift.
 */
export function appendVisibleAssociations(
  opts: ReadOptions,
  alreadyEmitted: Set<string>
): DetailField[] {
  const hiddenAssociations = new Set(opts.config.hiddenAssociations ?? []);
  const fields: DetailField[] = [];
  const consider = (name: string) => {
    if (alreadyEmitted.has(name)) {
      return;
    }
    alreadyEmitted.add(name);
    // Registry-hidden: another surface owns this relationship's rendering.
    if (hiddenAssociations.has(name)) {
      return;
    }
    if (opts.assocAccessMap.get(name) === 'internal') {
      return;
    }
    // Skip hidden targets (notes, files, meetings, etc.)
    if (isHiddenAssociation(name, opts.associationTargetMap)) {
      return;
    }
    if (!passesReadKindFilter(name, opts)) {
      return;
    }
    fields.push({ type: 'association', key: name });
  };
  for (const assoc of opts.object.associations ?? []) {
    consider(assoc.association_type_name);
  }
  // Associations the caller wants listed even with no target on the object
  // (create-form placeholders, just-cleared rows) — same filters apply.
  for (const name of opts.keepEmptyAssociationKeys ?? []) {
    consider(name);
  }
  return fields;
}

/**
 * Fallback field list used when an entity type has no `detailFieldOrder`:
 * all readable properties → all visible associations → timestamps.
 */
export function buildFallbackList(
  options: BuildFallbackFieldListOptions
): DetailField[] {
  const { mode, config, schema } = options;
  const { hiddenProperties = [] } = config;
  const hiddenSet = new Set(hiddenProperties);

  // Fallback (no detailFieldOrder)
  const result: DetailField[] = [];

  for (const key of Object.keys(schema)) {
    if (!isVisiblePropertyKey(key, mode, hiddenSet, schema)) {
      continue;
    }
    if (mode === 'read' && dropsEmptyProperty(key, options)) {
      continue;
    }
    result.push({ type: 'property', key });
  }

  if (mode === 'read') {
    result.push(...appendVisibleAssociations(options, new Set<string>()));

    // Timestamps honour hiddenProperties like every other row — the
    // automation slideout hides them here because its top zone already
    // renders them (RecordMetaSection).
    if (
      !hiddenSet.has('ahoy_created_at') &&
      getPropertyValue<unknown>(options.object.properties?.ahoy_created_at)
    ) {
      result.push({ type: 'timestamp', key: 'ahoy_created_at' });
    }
    if (
      !hiddenSet.has('ahoy_updated_at') &&
      getPropertyValue<unknown>(options.object.properties?.ahoy_updated_at)
    ) {
      result.push({ type: 'timestamp', key: 'ahoy_updated_at' });
    }
  } else {
    // edit mode fallback: all editable associations (minus registry-hidden —
    // an association another surface owns is not editable from this form
    // either, mirroring the read path).
    const hiddenAssociations = new Set(config.hiddenAssociations ?? []);
    for (const assocType of options.editableAssociationTypes) {
      if (hiddenAssociations.has(assocType.association_type_name)) {
        continue;
      }
      result.push({
        type: 'association',
        key: assocType.association_type_name,
      });
    }
  }

  return result;
}
